import React, { useState } from 'react';
import { Copy, RefreshCw, Linkedin, Lock, Check, Upload, FileText, AlertCircle } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

// Setting worker path for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const LinkedInOptimizer = ({ resumeData }) => {
    const { isPremium, hasFeature, purchaseFeature, getFeaturePrice } = useSubscription();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(null);

    // PDF Import State
    const [currentProfileText, setCurrentProfileText] = useState("");
    const [importing, setImporting] = useState(false);

    const isUnlocked = isPremium || hasFeature('linkedin_optimizer');
    const featurePrice = getFeaturePrice('linkedin_optimizer');

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImporting(true);
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            let fullText = "";

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map(item => item.str).join(" ");
                fullText += pageText + "\n";
            }

            setCurrentProfileText(fullText);
            // Auto-trigger generation if we have resume data
            if (resumeData) {
                handleGenerate(fullText);
            }
        } catch (error) {
            console.error("PDF Parse Error:", error);
            alert("Failed to read LinkedIn PDF. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    const handleGenerate = React.useCallback(async (profileText = currentProfileText) => {
        setLoading(true);
        // Get API Key from Local Storage (Client-side)
        const apiKey = localStorage.getItem('gemini_api_key');
        console.log("Generating with Key:", apiKey ? "Present" : "Missing");

        try {
            const response = await fetch('/api/linkedin-optimize', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resumeData,
                    currentProfile: profileText, // Send current profile for gap analysis
                    apiKey // Pass the key to the server
                })
            });
            const data = await response.json();
            if (response.ok) {
                setResult(data);
            } else {
                alert(data.error || "Generation failed");
            }
        } catch (e) {
            alert("Error: " + e.message);
        } finally {
            setLoading(false);
        }
    }, [resumeData, currentProfileText]);

    // LinkedIn OAuth Logic
    const handleConnectLinkedIn = () => {
        const width = 600;
        const height = 700;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;

        window.open(
            '/api/auth/linkedin/connect',
            'LinkedInAuth',
            `width=${width},height=${height},top=${top},left=${left}`
        );
    };

    React.useEffect(() => {
        const handleMessage = (event) => {
            if (event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
                const { profile, text } = event.data;
                console.log("LinkedIn Auth Success:", profile);

                // Set the basic text
                setCurrentProfileText(text);

                // Warn about fetching limitation (since API limits bio access)
                alert(`Connected as ${profile.name}! Note: LinkedIn's privacy API restricts accessing your full "About" section automatically. For the best "Gap Analysis", we still recommend uploading your PDF.`);

                if (resumeData) {
                    handleGenerate(text);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleGenerate]);



    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    if (!isUnlocked) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-6 bg-slate-50">
                <div className="bg-blue-100 p-4 rounded-full text-blue-600">
                    <Linkedin size={48} />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">LinkedIn Profile Optimizer</h2>
                <p className="max-w-md text-slate-600">
                    Stand out to recruiters with AI-generated viral headlines and a professional "About" section, tailored from your resume.
                </p>
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm max-w-sm w-full">
                    <ul className="text-left space-y-3 mb-6">
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                            <Check size={16} className="text-green-500" /> 3 Viral Headline Options
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                            <Check size={16} className="text-green-500" /> Professional Story Bio
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                            <Check size={16} className="text-green-500" /> Skimmable Bulleted Bio
                        </li>
                    </ul>
                    <button
                        onClick={() => purchaseFeature('linkedin_optimizer', featurePrice)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95"
                    >
                        <Lock size={16} /> Unlock for ${featurePrice}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-50">
            <div className="max-w-4xl mx-auto space-y-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <Linkedin className="text-blue-600" /> LinkedIn Optimizer
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">Upload your LinkedIn Profile PDF for a gap analysis, or generate from scratch.</p>
                    </div>
                </div>

                {/* Import Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Option 1: PDF Upload */}
                    <div className="bg-white p-6 rounded-xl border border-dashed border-slate-300 hover:border-blue-400 transition-colors text-center group relative">
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={handlePdfUpload}
                            className="hidden"
                            id="linkedin-pdf-upload"
                            disabled={loading || importing}
                        />
                        <label htmlFor="linkedin-pdf-upload" className="cursor-pointer block w-full h-full">
                            <div className="flex flex-col items-center gap-2">
                                {importing ? (
                                    <RefreshCw className="animate-spin text-blue-500" size={32} />
                                ) : currentProfileText && !currentProfileText.includes('Email:') ? ( // Simple heuristic for PDF vs OAuth text
                                    <FileText className="text-green-500" size={32} />
                                ) : (
                                    <Upload className="text-slate-400 group-hover:text-blue-500" size={32} />
                                )}

                                <p className="text-sm font-medium text-slate-700">
                                    {importing ? "Scanning..." : "Upload Profile PDF"}
                                </p>
                                <p className="text-xs text-slate-400">Best for deep analysis (includes Bio)</p>
                            </div>
                        </label>
                    </div>

                    {/* Option 2: OAuth Connect */}
                    <div
                        onClick={handleConnectLinkedIn}
                        className="bg-blue-50 p-6 rounded-xl border border-blue-200 hover:bg-blue-100 transition-colors text-center cursor-pointer flex flex-col items-center gap-2 justify-center"
                    >
                        <Linkedin className="text-blue-700" size={32} />
                        <p className="text-sm font-bold text-blue-800">Connect LinkedIn</p>
                        <p className="text-xs text-blue-600">Quick connect (Basic Info only)</p>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={() => handleGenerate(currentProfileText)}
                        disabled={loading || importing}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50 transition-all shadow-md active:scale-95"
                    >
                        {loading ? <RefreshCw className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                        {result ? 'Regenerate Analysis' : 'Analyze & Optimize'}
                    </button>
                </div>

                {loading && (
                    <div className="text-center py-20 text-slate-500 animate-pulse">
                        Analyzing resume and crafting your professional brand...
                    </div>
                )}

                {result && !loading && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">

                        {/* Gap Analysis Section */}
                        {result.gap_analysis && (
                            <div className="bg-amber-50 rounded-xl shadow-sm border border-amber-200 p-6">
                                <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                                    <AlertCircle size={20} />
                                    Profile Gap Analysis
                                </h3>
                                <div className="space-y-4">
                                    <div className="bg-white/50 p-4 rounded-lg border border-amber-100">
                                        <p className="text-amber-800 italic">"{result.gap_analysis.critique}"</p>
                                    </div>
                                    <div>
                                        <h4 className="text-xs font-bold text-amber-900/60 uppercase mb-2">Missing Keywords</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {result.gap_analysis.missing_keywords.map((kw, i) => (
                                                <span key={i} className="px-2 py-1 bg-white border border-amber-200 text-amber-800 text-xs rounded-md font-medium shadow-sm">
                                                    {kw}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Headlines Section */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <h3 className="text-lg font-bold text-slate-800 mb-4">Headline Options</h3>
                            <div className="space-y-4">
                                {result.headlines.map((headline, index) => (
                                    <div key={index} className="flex gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100 group">
                                        <div className="flex-1">
                                            <span className="text-xs font-bold text-slate-400 uppercase mb-1 block">
                                                Option {['Classic', 'Value-Driven', 'Creative'][index] || index + 1}
                                            </span>
                                            <p className="text-slate-800 font-medium">{headline}</p>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(headline, `head-${index}`)}
                                            className="text-slate-400 hover:text-blue-600 transition-colors"
                                            title="Copy"
                                        >
                                            {copied === `head-${index}` ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* About Section - Professional */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-slate-800">About Section (Story Format)</h3>
                                <button
                                    onClick={() => copyToClipboard(result.about_professional, 'about-pro')}
                                    className="text-slate-500 hover:text-blue-600 flex items-center gap-1 text-sm font-bold"
                                >
                                    {copied === 'about-pro' ? <Check size={16} /> : <Copy size={16} />} Copy
                                </button>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {result.about_professional}
                            </div>
                        </div>

                        {/* About Section - Skimmable */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-slate-800">About Section (Bulleted)</h3>
                                <button
                                    onClick={() => copyToClipboard(result.about_skimmable, 'about-skim')}
                                    className="text-slate-500 hover:text-blue-600 flex items-center gap-1 text-sm font-bold"
                                >
                                    {copied === 'about-skim' ? <Check size={16} /> : <Copy size={16} />} Copy
                                </button>
                            </div>
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {result.about_skimmable}
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default LinkedInOptimizer;
